/**
 * RGM Contests · Phase 1 — Admin Judging tab for a single contest.
 * Mounted at /admin/contests/:contestId/judging. Extends the existing
 * admin contest surface without touching the existing editor logic.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import CriteriaEditor from "@/components/contest/CriteriaEditor";
import JudgesTable from "@/components/contest/JudgesTable";

export default function AdminContestJudgingPage() {
  const { contestId } = useParams();
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [meta, setMeta] = useState({});
  const [judges, setJudges] = useState([]);
  const [results, setResults] = useState(null);
  const [q, setQ] = useState("");
  const [search, setSearch] = useState([]);
  const [saving, setSaving] = useState(false);
  const [criteriaDraft, setCriteriaDraft] = useState([]);
  const [winnerPicks, setWinnerPicks] = useState({});

  const reload = async () => {
    try {
      const c = await api.get(`/admin/contests/${contestId}/judging/config`);
      setConfig(c.data.config); setMeta({
        judging_closed_at: c.data.judging_closed_at,
        contest_status: c.data.contest_status,
        rubric_frozen: c.data.rubric_frozen,
      });
      setCriteriaDraft(c.data.config?.criteria || []);
      const j = await api.get(`/admin/contests/${contestId}/judges`);
      setJudges(j.data.judges || []);
      if (c.data.judging_closed_at) {
        const rr = await api.get(`/admin/contests/${contestId}/judging/results`);
        setResults(rr.data);
      }
    } catch (e) {
      if (e?.response?.status === 404) toast.error("Judging module disabled — turn on in Control Center");
      else toast.error("Failed to load judging config");
    }
  };
  useEffect(() => { reload(); }, [contestId]); // eslint-disable-line

  useEffect(() => {
    if (!q || q.length < 2) { setSearch([]); return; }
    let cancel = false;
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/users/search", { params: { q, limit: 8 } });
        if (!cancel) setSearch(r.data?.users || []);
      } catch { if (!cancel) setSearch([]); }
    }, 300);
    return () => { cancel = true; clearTimeout(t); };
  }, [q]);

  const saveCriteria = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/contests/${contestId}/judging/config`, {
        criteria: criteriaDraft, scale: config?.scale || { min: 1, max: 10 },
      });
      toast.success("Criteria saved"); reload();
    } catch (e) {
      const d = e?.response?.data?.detail || "Save failed";
      toast.error(String(d));
    } finally { setSaving(false); }
  };

  const addJudge = async (uid) => {
    try {
      await api.post(`/admin/contests/${contestId}/judges`, { judge_user_id: uid, assignment_scope: "all" });
      toast.success("Judge assigned"); setQ(""); setSearch([]); reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Assign failed");
    }
  };

  const removeJudge = async (uid) => {
    if (!window.confirm("Remove this judge?")) return;
    try {
      await api.delete(`/admin/contests/${contestId}/judges/${uid}`);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Remove failed"); }
  };

  const closeJudging = async () => {
    if (!window.confirm("Close judging? Judges can no longer edit or submit reviews after this.")) return;
    try {
      await api.post(`/admin/contests/${contestId}/judging/close`);
      toast.success("Judging closed — review aggregated results below.");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Close failed"); }
  };

  const setPick = (entryId, pos) => {
    setWinnerPicks((prev) => {
      const cleaned = Object.fromEntries(Object.entries(prev).filter(([k, v]) => v !== pos && k !== entryId));
      return pos == null ? cleaned : { ...cleaned, [entryId]: pos };
    });
  };

  const finalize = async () => {
    const winners = Object.entries(winnerPicks).map(([entry_id, position]) => ({ entry_id, position }));
    if (winners.length === 0) { toast.error("Pick at least one winner"); return; }
    if (!window.confirm(`Send ${winners.length} winner(s) to the existing finalization flow?`)) return;
    try {
      await api.post(`/admin/contests/${contestId}/pick-winners`, { winners, dry_run: false });
      toast.success("Winners finalized via existing pick-winners workflow");
      navigate(`/admin/contests/${contestId}/entries`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Finalize failed"); }
  };

  const canEditCriteria = !meta.rubric_frozen && !meta.judging_closed_at;

  return (
    <div className="min-h-screen bg-black text-white px-4 py-4" data-testid="admin-judging-page">
      <header className="max-w-3xl mx-auto flex items-center gap-2 mb-4">
        <button onClick={() => navigate(`/admin/contests/${contestId}`)} className="text-fuchsia-300 text-sm" data-testid="admin-judging-back">← Contest editor</button>
        <h1 className="text-lg font-black flex-1">🧑‍⚖️ Judging</h1>
        {meta.judging_closed_at
          ? <span className="text-[10px] px-2 py-0.5 rounded bg-amber-500/20 text-amber-200" data-testid="admin-judging-closed-badge">Closed {new Date(meta.judging_closed_at).toLocaleString()}</span>
          : <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-200" data-testid="admin-judging-open-badge">Judging {meta.contest_status === "judging" ? "OPEN" : "not yet in judging state"}</span>
        }
      </header>

      <section className="max-w-3xl mx-auto space-y-4">
        <div className="rounded border border-white/10 bg-white/[0.02] p-3" data-testid="admin-judging-criteria-section">
          <div className="flex items-center gap-2 mb-2">
            <p className="text-xs font-bold text-fuchsia-200">Criteria</p>
            {meta.rubric_frozen && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-200" data-testid="admin-rubric-frozen-badge">Rubric frozen (submitted review exists)</span>}
          </div>
          <CriteriaEditor
            criteria={criteriaDraft} scale={config?.scale || { min: 1, max: 10 }}
            disabled={!canEditCriteria}
            onChange={setCriteriaDraft}
          />
          <div className="mt-2">
            <button
              onClick={saveCriteria} disabled={saving || !canEditCriteria}
              className="px-3 py-1.5 rounded bg-fuchsia-600 text-xs font-bold hover:bg-fuchsia-500 disabled:opacity-30"
              data-testid="admin-criteria-save-btn"
            >Save criteria</button>
          </div>
        </div>

        <div className="rounded border border-white/10 bg-white/[0.02] p-3" data-testid="admin-judging-judges-section">
          <p className="text-xs font-bold text-fuchsia-200 mb-2">Judges</p>
          <input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search RGM artists by name / @handle / email"
            className="w-full bg-black/40 border border-white/15 rounded px-2 py-1 text-xs text-white focus:outline-none focus:border-fuchsia-400/70"
            data-testid="admin-judge-search"
            disabled={!!meta.judging_closed_at}
          />
          {search.length > 0 && (
            <ul className="mt-2 space-y-1" data-testid="admin-judge-search-results">
              {search.map((u) => (
                <li key={u.user_id}>
                  <button
                    onClick={() => addJudge(u.user_id)}
                    className="w-full text-left p-2 rounded bg-black/30 border border-white/10 hover:border-fuchsia-400/40 text-xs flex items-center gap-2"
                    data-testid={`admin-judge-add-${u.user_id}`}
                  >
                    <span className="flex-1">{u.artist_name || u.name || u.user_id}</span>
                    <span className="text-fuchsia-300 text-[10px]">Assign →</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="mt-3">
            <JudgesTable judges={judges} disabled={!!meta.judging_closed_at} onRemove={removeJudge} />
          </div>
        </div>

        {!meta.judging_closed_at && (
          <button
            onClick={closeJudging}
            className="w-full py-2 rounded border border-amber-500/50 text-amber-200 text-xs font-bold hover:bg-amber-500/10"
            data-testid="admin-close-judging-btn"
          >🔒 Close Judging (locks all reviews · does NOT close the contest)</button>
        )}

        {results && (
          <div className="rounded border border-white/10 bg-white/[0.02] p-3" data-testid="admin-judging-results-section">
            <p className="text-xs font-bold text-fuchsia-200 mb-2">Aggregated results (post-close)</p>
            <ul className="space-y-1">
              {results.rows.map((r, idx) => {
                // Phase CJ-15.8g — resolve the artist / track for the
                // admin post-close surface. Blind judging stays blind;
                // this identity is attached server-side only on this
                // admin-authorized endpoint.
                const ident = r.canonical_identity || {};
                const isDeleted = ident.source_state === "deleted";
                const artist = ident.artist_name
                  || (isDeleted ? null : (r.entry?.ref_snapshot || {}).artist_name)
                  || null;
                const title = ident.track_title
                  || (r.entry?.ref_snapshot || {}).title
                  || r.entry?.title
                  || null;
                const cover = ident.cover_url || null;
                return (
                <li key={r.entry_id} className="p-2 rounded border border-white/10 bg-black/30 flex items-center gap-2" data-testid={`admin-result-row-${r.entry_id}`}>
                  <span className="text-[10px] tabular-nums text-gray-500 w-6">#{idx + 1}</span>
                  {cover && (
                    <img
                      src={cover} alt=""
                      className="w-8 h-8 rounded object-cover border border-white/10 shrink-0"
                      data-testid={`admin-result-cover-${r.entry_id}`}
                    />
                  )}
                  <div className="flex-1 min-w-0" data-testid={`admin-result-identity-${r.entry_id}`}>
                    {isDeleted ? (
                      <div className="text-xs font-bold text-amber-300 truncate" data-testid={`admin-result-deleted-${r.entry_id}`}>
                        Deleted/Unavailable Submission
                      </div>
                    ) : (
                      <div className="text-xs font-bold text-white truncate">
                        {artist ? <span data-testid={`admin-result-artist-${r.entry_id}`}>{artist}</span> : <span className="text-gray-400">Unknown artist</span>}
                        {title && <>
                          <span className="text-gray-500 mx-1">—</span>
                          <span data-testid={`admin-result-title-${r.entry_id}`}>{title}</span>
                        </>}
                      </div>
                    )}
                    <div className="text-[10px] text-gray-500 truncate">
                      <span className="tabular-nums">avg {r.avg}</span>
                      <span className="mx-1">·</span>
                      <span className="tabular-nums">{r.review_count} reviews</span>
                      <span className="mx-1">·</span>
                      <code className="text-gray-600" data-testid={`admin-result-blind-id-${r.entry_id}`}>{r.entry_id}</code>
                    </div>
                  </div>
                  {[1, 2, 3].map((pos) => (
                    <button
                      key={pos}
                      onClick={() => setPick(r.entry_id, winnerPicks[r.entry_id] === pos ? null : pos)}
                      className={`w-7 h-7 rounded text-[10px] font-bold border ${winnerPicks[r.entry_id] === pos ? "border-fuchsia-400/70 bg-fuchsia-500/20 text-fuchsia-100" : "border-white/15 text-gray-400 hover:border-white/30"}`}
                      data-testid={`admin-result-pick-${r.entry_id}-${pos}`}
                    >{pos === 1 ? "🥇" : pos === 2 ? "🥈" : "🥉"}</button>
                  ))}
                </li>
                );
              })}
            </ul>
            <button
              onClick={finalize} disabled={Object.keys(winnerPicks).length === 0}
              className="mt-3 w-full py-2 rounded bg-fuchsia-600 text-xs font-bold hover:bg-fuchsia-500 disabled:opacity-30"
              data-testid="admin-finalize-btn"
            >Send to existing pick-winners workflow ({Object.keys(winnerPicks).length} picked)</button>
            <p className="text-[10px] text-gray-500 mt-2">
              Uses the canonical <code>POST /admin/contests/{`{cid}`}/pick-winners</code> endpoint — coins, notifications and Hall of Champions flow unchanged.
            </p>
          </div>
        )}
      </section>
    </div>
  );
}
